import { betbyVirtualSports } from '@/settings/sports-settings';
import { EventMode } from '@/types/menu-setting';
import { getSportSetting } from '@/services/settings.service';
import { homeEventModeIcons, DEFAULT_HOME_EVENT_MODE_ICON } from '@/settings/sports/sports-event-config';

export const getSportMainBetTypes = (sport: number) => {
	const sportSetting = getSportSetting(sport);
	return sportSetting.oddsPageFilterBetTypes.flatMap(item => item.betTypes);
}

const isVirtualSports = (sportId: number) => {
	return (
		(sportId >= 125 && sportId <= 127) ||
		(sportId >= 177 && sportId <= 179) ||
		(sportId >= 180 && sportId <= 186) ||
		(sportId >= 190 && sportId <= 198) ||
		[112, 147, 188, 108, 109].includes(sportId)
	);
};

const isBetbyVirtualSports = (sport: number) => {
	return betbyVirtualSports.includes(sport);
};

const isAnyVirtualSportsProduct = (sport: number) => {
	return isVirtualSports(sport) || isBetbyVirtualSports(sport);
};

const isKironVirtualSport = (sport: string | number | undefined) => {
	if (sport === undefined || sport === null) return false;
	const sportId = typeof sport === 'string' ? sport : sport.toString();
	return sportId === '180' || sportId === '108' || sportId === '109';
};

const isVirtualSoccerListEmpty = (virtualSoccerList?: number[]) => {
	return !virtualSoccerList || virtualSoccerList.length === 0;
};

const hasVirtualSoccerList = (virtualSoccerList?: number[]) => {
	return virtualSoccerList && virtualSoccerList.length > 0;
};

const isInVirtualSoccerList = (sport: number, virtualSoccerList?: number[]) => {
	return virtualSoccerList?.includes(sport) ?? false;
};

/** Menu row should collapse into one Virtual Sports sub-item (GameId 180, lbl_180). */
const shouldGroupVirtualSoccerMenuFace = (gameId: number, virtualSoccerList?: number[]) => {
	if (isVirtualSoccerListEmpty(virtualSoccerList)) {
		return false;
	}
	return isInVirtualSoccerList(gameId, virtualSoccerList) || gameId === 180;
};

const hasMultipleVirtualSoccerSports = (virtualSoccerList?: number[]) => {
	return virtualSoccerList && virtualSoccerList.length > 1;
};

const getFirstVirtualSoccerSport = (virtualSoccerList?: number[]) => {
	return virtualSoccerList && virtualSoccerList.length > 0 ? virtualSoccerList[0] : undefined;
};

/** Lobby virtual180 RouteTarget uses /180; menu generateRoute maps to first Kiron sport in virtualSoccerList. */
const resolveVirtual180RouteTarget = (routeTarget: string | undefined, virtualSoccerList?: number[]) => {
	if (!routeTarget || !hasVirtualSoccerList(virtualSoccerList)) {
		return routeTarget;
	}
	const firstVirtualSoccer = getFirstVirtualSoccerSport(virtualSoccerList)!;
	return routeTarget.replace(/\/180(\/|$)/, `/${firstVirtualSoccer}$1`);
};

const shouldShowVirtualSoccerSubmenu = (sport: number, virtualSoccerList?: number[]) => {
	return !isVirtualSoccerListEmpty(virtualSoccerList) && 
	isInVirtualSoccerList(sport, virtualSoccerList) && 
	hasMultipleVirtualSoccerSports(virtualSoccerList);
};

const shouldShowNewTagForKironSport = (sportId: number, virtualSoccerList?: number[]) => {
	// 只有 sportId = 180 時才進入特殊邏輯
	if (sportId === 180) {
		// 如果是 180 且只有 180（沒有 108 或 109），不顯示 new tag
		return hasVirtualSoccerList(virtualSoccerList) && (isInVirtualSoccerList(108, virtualSoccerList) || isInVirtualSoccerList(109, virtualSoccerList));
	}
	// 其他運動正常顯示 new tag
	return true;
};

/** Galaxy page-top-bar label: Kiron VS2 products use lbl_180 while URL may be 108/109. */
const getThirdPartyTopBarSportId = (sportId: number, virtualSoccerList?: number[]) => {
	if (shouldGroupVirtualSoccerMenuFace(sportId, virtualSoccerList)) {
		return 180;
	}
	return sportId;
};

const getKironSportNewTagId = (sportId: number, virtualSoccerList?: number[]) => {
	// 如果是 108 或 109，且 virtualSoccerList 中有這些值，使用 180 的 new tag 配置
	if ((sportId === 108 || sportId === 109) && isInVirtualSoccerList(sportId, virtualSoccerList)) {
		return 180;
	}
	// 如果是 180 且有 108 或 109 在列表中，也使用 180 的配置
	if (sportId === 180 && (isInVirtualSoccerList(108, virtualSoccerList) || isInVirtualSoccerList(109, virtualSoccerList))) {
		return 180;
	}
	// 其他情況使用原本的 sportId
	return sportId;
};

export const getHomeEventModeIcon = (modeId: number | string) => {
	return homeEventModeIcons[`M${modeId}` as EventMode] ?? DEFAULT_HOME_EVENT_MODE_ICON;
};

export const sportService = {
	getSportMainBetTypes,
	isVirtualSports,
	isBetbyVirtualSports,
	isAnyVirtualSportsProduct,
	isKironVirtualSport,
	isVirtualSoccerListEmpty,
	hasVirtualSoccerList,
	isInVirtualSoccerList,
	shouldGroupVirtualSoccerMenuFace,
	getThirdPartyTopBarSportId,
	hasMultipleVirtualSoccerSports,
	getFirstVirtualSoccerSport,
	resolveVirtual180RouteTarget,
	shouldShowVirtualSoccerSubmenu,
	shouldShowNewTagForKironSport,
	getKironSportNewTagId,
	getHomeEventModeIcon
};